/**
 * UpdateMemResult 组件
 * 用于显示 UPDATE_MEM 工具的执行结果
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProcessStep, isUpdateMemStep } from '../../../../../types';
import { FiEdit, FiCheckCircle, FiFolder, FiClock } from 'react-icons/fi';

interface UpdateMemResultProps {
  step: ProcessStep;
}

export const UpdateMemResult: React.FC<UpdateMemResultProps> = ({ step }) => {
  const { t } = useTranslation(['v3/chat', 'common']);

  // 类型守卫：确保是UPDATE_MEM工具结果
  if (!isUpdateMemStep(step)) {
    return null;
  }

  // 解析工具结果数据
  const parseToolResult = () => {
    try {
      if (!step.result) {
        return null;
      }

      const result = step.result;
      
      return {
        taskResults: result.taskResults || null,
        projectResults: result.projectResults || [],
      };
    } catch (error) {
      console.error('解析 UPDATE_MEM 结果失败:', error);
      return null;
    }
  };

  const resultData = parseToolResult();

  if (!resultData) {
    return null;
  }

  const { taskResults, projectResults } = resultData;

  // 统计任务数量
  const getTaskStats = () => {
    if (!taskResults) return null;
    
    if (Array.isArray(taskResults)) {
      return {
        total: taskResults.length,
        completed: taskResults.filter((task: any) => task.done).length,
        pending: taskResults.filter((task: any) => !task.done).length,
      };
    }
    
    // 如果是单个任务对象
    if (typeof taskResults === 'object') {
      return {
        total: 1,
        completed: taskResults.done ? 1 : 0,
        pending: taskResults.done ? 0 : 1,
      };
    }
    
    return null;
  };

  const taskStats = getTaskStats();

  return (
    <div className="border border-ios-gray5 dark:border-ios-dark-gray4 rounded-lg p-3 bg-blue-50 dark:bg-blue-900/20">
      {/* 更新结果标题 */}
      <div className="flex items-center gap-2 mb-3">
        <FiEdit className="w-4 h-4 text-blue-600 dark:text-blue-400" />
        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
          {t('v3/chat:processSteps.updateComplete', '更新完成')}
        </h4>
      </div>

      {/* 统计信息 */}
      <div className="space-y-2">
        {/* 任务统计 */}
        {taskStats && (
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm">
              <FiCheckCircle className="w-3 h-3 text-blue-600 dark:text-blue-400" />
              <span className="text-gray-700 dark:text-gray-300">
                {t('v3/chat:processSteps.processed', '处理了 {{count}} 个任务', { count: taskStats.total })}
              </span>
            </div>
            
            {(taskStats.completed > 0 || taskStats.pending > 0) && (
              <div className="flex items-center gap-4 text-sm ml-5">
                {taskStats.completed > 0 && (
                  <span className="text-gray-600 dark:text-gray-400">
                    {t('v3/chat:processSteps.completed', '已完成: {{count}}', { count: taskStats.completed })}
                  </span>
                )}
                {taskStats.pending > 0 && (
                  <span className="text-gray-600 dark:text-gray-400">
                    {t('v3/chat:processSteps.inProgress', '进行中: {{count}}', { count: taskStats.pending })}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* 项目统计 */}
        {projectResults && projectResults.length > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <FiFolder className="w-3 h-3 text-purple-600 dark:text-purple-400" />
            <span className="text-gray-700 dark:text-gray-300">
              {t('v3/chat:processSteps.involvedProjects', '涉及 {{count}} 个项目', { count: projectResults.length })}
            </span>
          </div>
        )}

        {/* 更新时间 */}
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <FiClock className="w-3 h-3" />
          <span>{new Date(step.timestamp).toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

export default UpdateMemResult;
